import {
	type FeatureFlagKey,
	isFeatureFlagEnabled,
} from '@/shared/core/hooks/use-feature-flag/use-feature-flag.ts'
import {
	type I18nKeys,
	useTranslation,
} from '@/shared/core/hooks/use-translation/use-translation.ts'
import { useScrollContext } from '@/shared/provider/scroll-context/scroll-context.tsx'
import type { SectionId } from '@/teams/portfolio/types/section-id.ts'
import '@/teams/portfolio/components/nav-bar/nav-bar.scss'

interface NavItem {
	featureFlag?: FeatureFlagKey
	id: SectionId
	labelKey: I18nKeys
}

const NAV_ITEMS: NavItem[] = [
	{ id: 'home', labelKey: 'portfolio.nav.home' },
	{
		featureFlag: 'portfolio.section.skills.enabled',
		id: 'skills',
		labelKey: 'portfolio.nav.skills',
	},
	{
		featureFlag: 'portfolio.section.experience.enabled',
		id: 'experience',
		labelKey: 'portfolio.nav.experience',
	},
	{
		featureFlag: 'portfolio.section.projects.enabled',
		id: 'projects',
		labelKey: 'portfolio.nav.projects',
	},
	{
		featureFlag: 'portfolio.section.contact.enabled',
		id: 'contact',
		labelKey: 'portfolio.nav.contact',
	},
]

interface NavBarProps {
	activeSection: SectionId
}

export function NavBar({ activeSection }: Readonly<NavBarProps>) {
	const { t } = useTranslation()
	const { startScrolling } = useScrollContext()

	const visibleItems = NAV_ITEMS.filter(
		(item) => !item.featureFlag || isFeatureFlagEnabled(item.featureFlag),
	)

	const handleNavClick = (sectionId: SectionId) => {
		const element = document.getElementById(sectionId)
		if (element) {
			// Set flag to prevent section observer from updating URL during scroll
			startScrolling()

			// Update URL
			const params = new URLSearchParams(window.location.search)
			params.set('section', sectionId)
			const newUrl = `${window.location.pathname}?${params.toString()}`
			window.history.replaceState({}, '', newUrl)

			// Scroll to section
			element.scrollIntoView({ behavior: 'smooth' })
		}
	}

	const getLinkClasses = (itemId: SectionId) => {
		const classes = ['nav-bar__link']
		if (activeSection === itemId) {
			classes.push('nav-bar__link--active')
		}
		return classes.join(' ')
	}

	if (visibleItems.length === 0) {
		return null
	}

	return (
		<nav
			aria-label={t('portfolio.nav.ariaLabel')}
			className='nav-bar'
			data-testid={'nav-bar'}>
			<ul className='nav-bar__list'>
				{visibleItems.map((item) => (
					<li
						className='nav-bar__item'
						data-testid={`nav-bar__item_${item.id}`}
						key={item.id}>
						<button
							className={getLinkClasses(item.id)}
							onClick={() => handleNavClick(item.id)}
							type='button'>
							{t(item.labelKey)}
						</button>
					</li>
				))}
			</ul>
		</nav>
	)
}
